/**
 * v-reveal —— 全站统一的滚动入场指令
 *
 * 设计要点
 * --------
 * 1. 全站共享【一个】IntersectionObserver，而不是每个元素一个（降低开销）。
 * 2. 只切换一个类名（rv-on），具体动效全部交给 CSS 的 opacity / transform 完成，
 *    不触发布局计算。
 * 3. 进入视口后立即 unobserve —— 元素离开视口再回来不会重播，避免闪烁与干扰。
 * 4. prefers-reduced-motion: reduce 时不做任何处理，元素保持文档流中的自然可见状态，
 *    即"减少动态效果"用户看到的是静态页面。
 *
 * 用法
 * ----
 *   <h2 v-reveal>…</h2>                       默认 fade-slide（向上轻移淡入）
 *   <p  v-reveal="'stagger'">…</p>            子元素依次入场
 *   <div v-reveal="'fade'">…</div>            仅淡入
 *   <div v-reveal="'scale'">…</div>           轻微放大淡入（用于图形区）
 *
 * 错峰位移量用 CSS 自定义属性控制（写在各自组件的 scoped 样式里）：
 *   .my-item { --rv-y: 22px; }
 *   .my-item:nth-child(2) { --rv-delay: .08s; }
 */

const VARIANT = {
  fade: 'fade',
  'fade-slide': 'fade-slide',
  up: 'fade-slide',
  stagger: 'stagger',
  scale: 'scale'
}

// 元素进入视口即触发（threshold 设为 0，避免超高区块需要滚动很久才触发）
const ROOT_MARGIN = '0px 0px -10% 0px'

let observer = null

function ensureObserver () {
  if (observer || typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') return observer
  observer = new IntersectionObserver((entries) => {
    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i]
      if (!entry.isIntersecting) continue
      const el = entry.target
      el.classList.add('rv-on')
      // 只播一次：立即停止观察，避免离开视口后重播
      observer.unobserve(el)
    }
  }, { threshold: 0, rootMargin: ROOT_MARGIN })
  return observer
}

function prefersReducedMotion () {
  return typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function resolveVariant (binding) {
  const raw = binding && binding.value != null ? binding.value : 'fade-slide'
  return VARIANT[raw] || 'fade-slide'
}

export default {
  inserted (el, binding) {
    // 减少动态效果：不挂任何状态类，元素保持自然可见
    if (prefersReducedMotion()) return

    el.setAttribute('data-rv', resolveVariant(binding))
    el.classList.add('rv')

    const ob = ensureObserver()
    if (!ob) {
      // 浏览器不支持 IntersectionObserver：直接显示，保证内容可读
      el.classList.add('rv-on')
      return
    }
    ob.observe(el)
  },

  unbind (el) {
    if (observer) observer.unobserve(el)
    el.classList.remove('rv', 'rv-on')
    el.removeAttribute('data-rv')
  }
}

export { observer }



// WEBPACK FOOTER //
// ./src/directives/reveal.js